import { WorkspaceClosedError } from '../router/spaces-resources'
import { updatePageEmbeddings, type PageEmbeddingUpdateReport, type PendingPageEmbedding } from './page-embeddings'
import { createPageEmbeddingsWorkerClient } from './page-embeddings-worker-client'
import { runtimeMonitor } from '@w/runtime'
import { PAGE_EMBEDDINGS_DB_TASK_ID } from './page-embeddings-db-task-id'
import type { PageEmbeddingsBrowserClientConfig, PageEmbeddingsBrowserClient } from './page-embeddings-browser.types'
export type { PageEmbeddingsBrowserClientConfig, PageEmbeddingsBrowserClient } from './page-embeddings-browser.types'

type PageEmbeddingsRemote = {
  updateDirtyPages(workspace?: { workspaceId: string }): Promise<PageEmbeddingUpdateReport>
}

// The browser coordinates short database tasks; the model runs in its own worker.
export function createPageEmbeddingsBrowserClient(
  args: PageEmbeddingsBrowserClientConfig,
): PageEmbeddingsBrowserClient {
  let workerSupported: Promise<boolean> | undefined
  let embeddings: ReturnType<NonNullable<PageEmbeddingsBrowserClientConfig['createEmbeddings']>> | undefined
  let closed = false
  const cancelled = Promise.withResolvers<PageEmbeddingUpdateReport>()
  const empty = { stored: 0, stale: 0, total: 0 }
  const client = createPageEmbeddingsBrowserClientFromRemote({
    updateDirtyPages() {
      return runtimeMonitor.run({ name: 'Update search index', kind: 'task', detail: args.cacheDir }, () => Promise.race([cancelled.promise, update()]))
    },
  }, {
    ...args,
    onError: args.onError ?? ((error) => {
      console.warn('Page embedding maintenance disabled after background failure.', error)
    }),
  })
  async function update() {
    if (closed) return empty
    workerSupported ??= (args.isWorkerSupported ?? hasUsableWebGpu)()
    if (!(await workerSupported) || closed) return empty
    return args.tel.span({ name: 'embeddings.index', input: undefined }, async tel => {
      try {
        const result = await updatePageEmbeddings({
          read: limit => args.driver.runWorkerTask<PendingPageEmbedding[]>(PAGE_EMBEDDINGS_DB_TASK_ID, { action: 'read', limit }, { tel }),
          store: rows => args.driver.runWorkerTask<number[]>(PAGE_EMBEDDINGS_DB_TASK_ID, { action: 'store', rows }, { tel }),
          isClosed: () => closed,
          embeddings: { embedDocument(text) {
            embeddings ??= args.createEmbeddings?.() ?? createPageEmbeddingsWorkerClient(args.cacheDir)
            return embeddings.embedDocument(text, tel)
          } },
        })
        return result
      } catch (error) {
        if (closed || error instanceof WorkspaceClosedError) return empty
        throw error
      }
    })
  }
  return { ...client, close() {
    if (closed) return
    closed = true
    client.close()
    embeddings?.close()
    cancelled.resolve(empty)
  } }
}

export function createPageEmbeddingsBrowserClientFromRemote(
  remote: PageEmbeddingsRemote,
  args: {
    cacheDir: string
    debounceMs?: number
    onError?: (error: unknown) => void
  },
): PageEmbeddingsBrowserClient {
  let timer: ReturnType<typeof setTimeout> | undefined
  let current = Promise.resolve()
  let backfillStarted = false
  let disabled = false
  let pendingWorkspace: { workspaceId: string } | undefined
  const debounceMs = args.debounceMs ?? 1_000

  function schedule(): void {
    if (disabled) return
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => {
      timer = undefined
      current = runAfterCurrent(
        current,
        runPending,
        disableAfterFailure,
      )
    }, debounceMs)
  }

  function runPending() {
    const workspace = pendingWorkspace
    pendingWorkspace = undefined
    return workspace ? remote.updateDirtyPages(workspace) : remote.updateDirtyPages()
  }

  function disableAfterFailure(error: unknown): void {
    disabled = true
    args.onError?.(error)
  }

  return {
    startBackfill() {
      if (backfillStarted) return
      backfillStarted = true
      schedule()
    },
    update(workspace) {
      pendingWorkspace = workspace
      schedule()
    },
    async waitForIdle() {
      if (timer) {
        clearTimeout(timer)
        timer = undefined
        current = runAfterCurrent(
          current,
          runPending,
          disableAfterFailure,
        )
      }
      await current
    },
    close() {
      disabled = true
      if (timer) clearTimeout(timer)
      timer = undefined
      backfillStarted = true
    },
  }
}

async function runAfterCurrent(
  current: Promise<void>,
  run: () => Promise<unknown>,
  onError: (error: unknown) => void,
): Promise<void> {
  await current
  try {
    await run()
  } catch (error) {
    onError(error)
  }
}

type NavigatorWithGpu = {
  gpu?: {
    requestAdapter(): Promise<unknown>
  }
}

export async function hasUsableWebGpu(
  navigatorLike: NavigatorWithGpu = navigator,
): Promise<boolean> {
  try {
    return Boolean(await navigatorLike.gpu?.requestAdapter())
  } catch {
    return false
  }
}
